/**
 * User Modal Component - Using FormModal with Unsaved Changes Detection
 */
import React, { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { FormModal } from '../../components/ui/FormModal';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';

const userSchema = z.object({
  name: z.string().min(1, 'Nome é obrigatório').max(100, 'Nome muito longo'),
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Mínimo 6 caracteres').optional().or(z.literal('')),
  role: z.string().min(1, 'Cargo é obrigatório'),
  profile_id: z.string().optional(),
  is_active: z.boolean().optional(),
});

export function UserModal({ open, onOpenChange, user, profiles = [], onSave, loading }) {
  const { t } = useTranslation();
  const isEditing = !!user?.id;

  const { register, handleSubmit, control, reset, watch, formState: { errors, isDirty } } = useForm({
    resolver: zodResolver(userSchema),
    defaultValues: {
      name: '',
      email: '',
      password: '',
      role: 'funcionario',
      profile_id: '',
      is_active: true,
    },
  });

  // Reset form when modal opens or user changes
  useEffect(() => {
    if (!open) return; // Only reset when modal is opening
    
    if (user) {
      reset({
        name: user.name || '',
        email: user.email || '',
        password: '',
        role: user.role || 'funcionario',
        profile_id: user.profile_id || '',
        is_active: user.is_active ?? true,
      });
    } else {
      reset({
        name: '',
        email: '',
        password: '',
        role: 'funcionario',
        profile_id: '',
        is_active: true,
      });
    }
  }, [open, user, reset]);

  const formValues = watch();

  const handleLoadDraft = (draft) => {
    reset(draft);
  };

  const onSubmit = (data) => {
    onSave(data);
  };

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title={isEditing ? t('users.editUser') : t('users.newUser')}
      description={isEditing ? 'Atualize os dados do usuário' : 'Preencha os dados do novo usuário'}
      size="default"
      formId="user"
      entityId={user?.id}
      isDirty={isDirty}
      formValues={formValues}
      onLoadDraft={handleLoadDraft}
      onSave={handleSubmit(onSubmit)}
      loading={loading}
      confirmText={t('common.save')}
      cancelText={t('common.cancel')}
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="name">{t('users.name')} *</Label>
          <Input
            id="name"
            {...register('name')}
            placeholder="Nome completo"
            data-testid="user-name-input"
          />
          {errors.name && <p className="text-sm text-destructive">{errors.name.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">{t('users.email')} *</Label>
          <Input
            id="email"
            type="email"
            {...register('email')}
            placeholder="email@exemplo.com"
            data-testid="user-email-input"
          />
          {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">
            {isEditing ? 'Nova Senha (deixe em branco para manter)' : `${t('users.password')} *`}
          </Label>
          <Input
            id="password"
            type="password"
            {...register('password')}
            placeholder="••••••••"
            data-testid="user-password-input"
          />
          {errors.password && <p className="text-sm text-destructive">{errors.password.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="role">{t('users.role')} *</Label>
            <Controller
              name="role"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger data-testid="user-role-select">
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="funcionario">Funcionário</SelectItem>
                    <SelectItem value="garcom">Garçom</SelectItem>
                  </SelectContent>
                </Select>
              )}
            />
            {errors.role && <p className="text-sm text-destructive">{errors.role.message}</p>}
          </div>

          <div className="space-y-2">
            <Label htmlFor="profile_id">Perfil</Label>
            <Controller
              name="profile_id"
              control={control}
              render={({ field }) => (
                <Select 
                  value={field.value || 'none'} 
                  onValueChange={(val) => field.onChange(val === 'none' ? '' : val)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nenhum</SelectItem>
                    {profiles.map(profile => (
                      <SelectItem key={profile.id} value={profile.id}>
                        {profile.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </div>
        </div>

        <Controller
          name="is_active"
          control={control}
          render={({ field }) => (
            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={field.value}
                onCheckedChange={field.onChange}
              />
              <Label htmlFor="is_active">{t('common.active')}</Label>
            </div>
          )}
        />
      </div>
    </FormModal>
  );
}

export default UserModal;
